// src/components/ProductTable.jsx
import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Table,
  message,
  Modal,
  Tooltip,
  Button,
  Form,
  Input,
  InputNumber,
  Spin,
  Image,
  Select,
  Card,
  Tag,
  Avatar,
  Descriptions,
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FilterOutlined,
  SearchOutlined,
  ShoppingOutlined,
  EuroOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  PoundOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";
import { ProductContext } from "../contexts/ProductContext";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
const quillModules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    ["blockquote", "code-block"],
    [{ header: 1 }, { header: 2 }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ script: "sub" }, { script: "super" }],
    ["link", "image"],
    ["clean"],
  ],
};

const { Option } = Select;

const ExpandableText = ({ text }) => {
  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html || "";
    return div.textContent || div.innerText || "";
  };

  const plain = stripHtml(text);

  return (
    <div
      style={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
      }}
      title={plain}
    >
      {plain}
    </div>
  );
};

const ProductTable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const rawRole = localStorage.getItem("userRole") || "";
  const role = rawRole.toLowerCase();
  const isSuperAdmin = role === "super admin" || role === "super_admin";

  const {
    products,
    loading,
    pagination,
    fetchProducts,
    sortType,
    setSortType,
    searchTerm,
    setSearchTerm,
  } = useContext(ProductContext);

  const [editVisible, setEditVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [quickDesc, setQuickDesc] = useState("");
  const [form] = Form.useForm();
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  // loading state for quick-edit fetch + save
  const [editLoading, setEditLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Per-size editable rows in quick-edit modal
  const [quickSizes, setQuickSizes] = useState([]);

  // mounted ref to avoid setState on unmounted component
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!sortType) {
      setSortType("created-desc");
    }
  }, [sortType, setSortType]);

  // Refetch after add/edit when navigated back with state
  useEffect(() => {
    if (location.state?.updatedProduct) {
      fetchProducts(pagination.current);
      // clear the state so it doesn't refire on next render
      navigate("/products", { replace: true });
    }
  }, [location.state, navigate, fetchProducts, pagination.current]);

  const handleTableChange = (paginationConfig) => {
    fetchProducts(paginationConfig.current);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this product?",
      content: "This action cannot be undone.",
      okType: "danger",
      onOk: async () => {
        try {
          const token = localStorage.getItem("token");
          await axiosInstance.delete(`/admin/product/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success("Product Deleted Successfully");

          const isLastItemOnPage = products.length === 1;
          const isNotFirstPage = pagination.current > 1;
          const newCurrent = isLastItemOnPage && isNotFirstPage ? pagination.current - 1 : pagination.current;

          fetchProducts(newCurrent);
        } catch {
          message.error("Delete failed");
        }
      },
    });
  };

  // Fixed: Better error handling with fallback to existing data
  const handleQuickEdit = async (rec) => {
    setEditing(rec);
    setQuickDesc("");
    setQuickSizes([]);
    form.resetFields();
    setEditVisible(true);
    setEditLoading(true);
    setInitialLoadDone(false);

    try {
      const token = localStorage.getItem("token");

      const res = await axiosInstance.get(`/product/${rec.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data?.success && res.data?.product) {
        const product = res.data.product;
        const desc = product.description || "";

        // Build quickSizes from product.Sizes (best-effort mapping; adapt to your backend names if needed)
        const sizesFromProduct = (product.Sizes || []).map((s) => ({
          sizeId: Number(s.id),
          label: s.size || (s.label || `Size ${s.id}`),
          // sample possible fields - adjust keys if backend uses different names
          cost_price: s.factory_price !== undefined ? Number(s.factory_price) : (s.cost_price !== undefined ? Number(s.cost_price) : 0),
          sale_price: s.price_euro !== undefined ? Number(s.price_euro) : (s.sale_price !== undefined ? Number(s.sale_price) : 0),
          stock: Number(s.stock ?? 0),
          length: s.length ?? "",
          width: s.width ?? "",
          height: s.height ?? "",
        }));

        // if no Sizes found, create a fallback single-row representation using product-level values
        const finalSizes = sizesFromProduct.length ? sizesFromProduct : [
          {
            sizeId: product.id,
            label: product.product_name || "Default",
            cost_price: Number(product.factory_price ?? 0),
            sale_price: Number(product.price_euro ?? 0),
            stock: Number(product.stock ?? 0),
            length: product.length ?? "",
            width: product.width ?? "",
            height: product.height ?? "",
          },
        ];

        setQuickSizes(finalSizes);

        setQuickDesc(desc);

        // Keep form fields for product name (description & sizes are handled separately)
        form.setFieldsValue({
          product_name: product.product_name,
          description: desc,
        });
      }
    } catch (err) {
      console.error("Quick edit error", err);
      message.error("Failed to load product details for quick edit");
    } finally {
      setEditLoading(false);
      setInitialLoadDone(true);
    }
  };

  // keep the editor value in state AND update the antd form field
  const handleQuillChange = (value) => {
    setQuickDesc(value);
    form.setFieldsValue({ description: value });
  };

  // Update a single size row in quickSizes
  const updateQuickSizeRow = (sizeId, partial) => {
    setQuickSizes((prev) =>
      prev.map((r) => (Number(r.sizeId) === Number(sizeId) ? { ...r, ...partial } : r))
    );
  };

  const computeTotalStock = () => {
    return quickSizes.reduce((sum, r) => sum + (Number(r.stock) || 0), 0);
  };

  const handleSaveQuickEdit = async () => {
    if (!editing || !editing.id) {
      message.error("No product selected");
      return;
    }

    setEditLoading(true);
    try {
      // Validate form product_name & description (ReactQuill content validated via plain-text)
      const vals = await form.validateFields(["product_name", "description"]).catch((e) => {
        // if validator rejected, still allow proceeding if quickDesc has content
        return form.getFieldsValue();
      });

      // Build sizes payload - adapt the property names to match what your backend expects.
      // Here we send explicit fields: sizeId, sale_price, cost_price, stock, length, width, height.
      const sizesPayload = quickSizes.map((s) => ({
        sizeId: Number(s.sizeId),
        // adapt the field names below if your backend expects e.g. price_euro / factory_price
        sale_price: s.sale_price !== undefined ? Number(s.sale_price) : 0,
        cost_price: s.cost_price !== undefined ? Number(s.cost_price) : 0,
        stock: Number(s.stock || 0),
        length: s.length === "" ? null : String(s.length),
        width: s.width === "" ? null : String(s.width),
        height: s.height === "" ? null : String(s.height),
      }));

      const totalStock = computeTotalStock();

      const updateData = {
        product_name: vals.product_name,
        description: quickDesc || vals.description || "",
        stock: totalStock,
        sizes: sizesPayload,
      };

      const token = localStorage.getItem("token");
      const res = await axiosInstance.put(
        `/admin/product/${editing.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data && res.data.success) {
        message.success("Product Updated Successfully");
        setEditVisible(false);
        setEditing(null);
        setQuickDesc("");
        setQuickSizes([]);
        form.resetFields();
        fetchProducts(pagination.current);
      } else {
        console.error("Save response:", res.data);
        throw new Error(res.data?.message || "Update failed");
      }
    } catch (err) {
      console.error("Save quick edit error", err);
      message.error("Update failed: " + (err.message || "Please check console"));
    } finally {
      setEditLoading(false);
    }
  };

  const handleFullEdit = (product) => {
    navigate(`/edit-product/${product.id}`);
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setDetailsVisible(true);
  };

  const getInitials = (name) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
      : "P";
  };

  const getStockColor = (stock) => {
    if (stock === null || stock === undefined) return "gray";
    if (stock === 0) return "red";
    if (stock < 10) return "orange";
    return "green";
  };

  const getStockText = (stock) => {
    if (stock === null || stock === undefined) return "Unknown";
    if (stock === 0) return "Out of Stock";
    if (stock < 10) return "Low Stock";
    return "In Stock";
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "" || amount === "0") return "0";
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) return "0";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericAmount);
  };

  // Handle sending selected products to inventory
  const handleSendToInventory = async () => {
    const productsToSend = selectedRowKeys.filter((id) => {
      const product = products.find((p) => p.id === id);
      return !product?.isInventoryPart;
    });

    if (productsToSend.length === 0) {
      message.warning("All selected products are already in inventory");
      return;
    }

    setInventoryLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        "/admin/products/mark-inventory",
        {
          productIds: productsToSend,
          isInventoryPart: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success(`Successfully sent ${productsToSend.length} product(s) to inventory`);
      setSelectedRowKeys([]);
      fetchProducts(pagination.current); // Refresh the list
    } catch (error) {
      message.error("Failed to send products to inventory");
    } finally {
      setInventoryLoading(false);
    }
  };

  // Handle select all/none - only select non-inventory products
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const nonInventoryIds = products.filter((product) => !product.isInventoryPart).map((product) => product.id);
      setSelectedRowKeys(nonInventoryIds);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const nonInventoryProducts = products.filter((product) => !product.isInventoryPart);
  const isAllSelected = nonInventoryProducts.length > 0 && selectedRowKeys.length === nonInventoryProducts.length;
  const isSomeSelected = selectedRowKeys.length > 0 && selectedRowKeys.length < nonInventoryProducts.length;

  // ---------- DRAFTS: state + functions ----------
  const [draftsModalOpen, setDraftsModalOpen] = useState(false);
  const [draftsList, setDraftsList] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);

const openDraftsModal = async () => {
  setDraftsModalOpen(true);
  setLoadingDrafts(true);

  try {
    const [prodRes, bedRes] = await Promise.all([
      axiosInstance.get("/admin/drafts?type=product"),
      axiosInstance.get("/admin/drafts?type=bed"),
    ]);

    const prodDrafts = prodRes.data?.drafts || [];
    const bedDrafts = bedRes.data?.drafts || [];

    const merged = [
      ...prodDrafts.map((d) => ({ ...d, draftType: d.draftType || "product" })),
      ...bedDrafts.map((d) => ({ ...d, draftType: d.draftType || "bed" })),
    ].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

    setDraftsList(merged);
  } catch (err) {
    console.error("fetch drafts", err);
    message.error("Failed to load drafts");
    setDraftsList([]);
  } finally {
    setLoadingDrafts(false);
  }
};

  const continueDraft = (draft) => {
  const id = draft.id || draft.draftId || draft._id;
  const type = String(draft.draftType || "product").toLowerCase();

  if (type === "bed") {
    navigate(`/add-new-bed?draftId=${id}`);   // ✅ correct bed route
  } else {
    navigate(`/add-product?draftId=${id}`);   // ✅ product route
  }
};

  const deleteDraft = async (draftId) => {
    try {
      await axiosInstance.delete(`/admin/drafts/${draftId}`);
      message.success("Draft deleted");
      setDraftsList((prev) => prev.filter((d) => (d.id || d.draftId) !== draftId));
    } catch (err) {
      console.error("delete draft", err);
      message.error("Failed to delete draft");
    }
  };
  // ---------- end drafts ----------

  const columns = [
    {
      title: (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={isAllSelected}
            ref={(input) => {
              if (input) {
                input.indeterminate = isSomeSelected;
              }
            }}
            onChange={handleSelectAll}
          />
        </div>
      ),
      key: "selection",
      width: 60,
      render: (_, record) => (
        <div className="flex items-center justify-center">
          {record.isInventoryPart ? (
            <Tooltip title="Already in inventory">
              <CheckCircleOutlined className="text-green-500" />
            </Tooltip>
          ) : (
            <input
              type="checkbox"
              checked={selectedRowKeys.includes(record.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedRowKeys((prev) => [...prev, record.id]);
                } else {
                  setSelectedRowKeys((prev) => prev.filter((id) => id !== record.id));
                }
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: "ID",
      key: "srNo",
      width: 50,
      render: (_, __, index) => <div className="text-gray-500 font-mono text-sm">#{(pagination.current - 1) * pagination.pageSize + index + 1}</div>,
    },
    {
      title: "Product",
      key: "product",
      width: 150,
      render: (_, record) => (
        <div className="flex items-center space-x-3">
          {record.product_image ? (
            <Image
              src={record.product_image}
              preview={false}
              style={{
                width: 40,
                height: 40,
                borderRadius: 6,
                objectFit: "cover",
              }}
              fallback="https://via.placeholder.com/40"
            />
          ) : (
            <Avatar size={40} className="bg-gradient-to-r from-blue-500 to-blue-600 shadow-md" icon={getInitials(record.product_name)} />
          )}
          <div className="min-w-0 flex-1">
            <div
              className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors text-sm truncate"
              onClick={() => handleFullEdit(record)}
              title={record.product_name}
            >
              {record.product_name}
            </div>
            <div className="text-xs text-gray-500 max-w-[180px]">
              <ExpandableText text={record.description} />
            </div>
            {record.isInventoryPart && (
              <Tag color="green" className="text-xs mt-1">
                In Inventory
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Stock Status",
      key: "stock_status",
      width: 120,
      render: (_, record) => {
        const stock = record.stock;
        const stockColor = getStockColor(stock);
        const stockText = getStockText(stock);

        return (
          <Tooltip title={`Available Stock: ${stock !== null && stock !== undefined ? stock : "Unknown"}`}>
            <div className="flex flex-col gap-1">
              <Tag color={stockColor} className="text-xs font-medium w-full text-center border-0">
                {stockText}
              </Tag>
              <div className="text-xs text-gray-500 text-center bg-gray-100 rounded px-1 py-0.5">Qty: {stock !== null && stock !== undefined ? stock : "N/A"}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Type(s)",
      dataIndex: "types",
      key: "types",
      width: 100,
      render: (types) => {
        const list = Array.isArray(types) ? types : types ? [types] : [];
        const labelize = (t) => {
          const s = String(t || "").toLowerCase();
          if (s.includes("non") && s.includes("ottoman")) return "Non-Ottoman";
          if (s.includes("ottoman")) return "Ottoman";
          if (s.includes("cloud")) return "Cloud";
          return t || "-";
        };

        if (!list.length) return <span className="text-gray-400">-</span>;

        return (
          <div className="flex flex-col gap-1 max-w-[160px]">
            {list.map((t, i) => (
              <div key={`${t}-${i}`} className="text-xs bg-gray-100 text-gray-800 rounded px-2 py-1 border border-gray-200 whitespace-nowrap overflow-hidden text-ellipsis" title={labelize(t)}>
                {labelize(t)}
              </div>
            ))}
          </div>
        );
      },
    },
    // ✅ Show Cost Price column only for super admin
    ...(isSuperAdmin ? [
      {
        title: "Cost Price",
        dataIndex: "factory_price",
        key: "fp",
        width: 110,
        render: (v) => (
          <div className="flex items-center space-x-2">
            <PoundOutlined className="text-gray-400" />
            <span className="font-semibold text-gray-900">{formatCurrency(v)}</span>
          </div>
        ),
      },
    ] : []),
    {
      title: "Sale Price",
      dataIndex: "price_euro",
      key: "sp",
      width: 110,
      render: (v) => (
        <div className="flex items-center space-x-2">
          <EuroOutlined className="text-green-500" />
          <span className="font-semibold text-green-600">{formatCurrency(v)}</span>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div className="flex items-center space-x-1">
          <Tooltip title="Quick Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined className="text-blue-600" />}
              onClick={() => handleQuickEdit(record)}
              className="hover:bg-blue-50 rounded-lg transition-colors"
            />
          </Tooltip>
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined className="text-green-600" />}
              onClick={() => handleViewDetails(record)}
              className="hover:bg-green-50 rounded-lg transition-colors"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} className="hover:bg-red-50 rounded-lg transition-colors" />
          </Tooltip>
        </div>
      ),
    },
  ];

  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" className="text-blue-600" />
          <p className="mt-3 text-gray-600 font-medium">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 lg:p-6">
      {/* Scoped Quill CSS */}
      <style>{`
        .quill-quick .ql-container {
          min-height: 180px !important;
          max-height: 300px !important;
          overflow-y: auto !important;
        }
        .quill-quick .ql-editor {
          min-height: 150px !important;
          white-space: pre-wrap !important;
          word-break: break-word !important;
        }
        .quill-quick .ql-editor p { margin: 0 0 8px 0 !important; }
        .quill-quick .ql-editor p:last-child { margin-bottom: 0 !important; }
        .sizes-grid input, .sizes-grid .ant-input-number { width: 100%; }
      `}</style>

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-white rounded-xl shadow-sm">
                <ShoppingOutlined className="text-blue-600 text-xl" />
              </div>
              Product Management
            </h1>
            <p className="text-gray-600 mt-2">Manage and monitor all products in your inventory</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-gray-600">Total:</span>
                <span className="font-semibold text-gray-900">{pagination.total || 0} products</span>
              </div>
            </div>

            {selectedRowKeys.length > 0 && (
              <Button type="primary" icon={<InboxOutlined />} loading={inventoryLoading} onClick={handleSendToInventory} className="bg-green-600 hover:bg-green-700 border-0 h-11 px-6 rounded-xl font-medium">
                Send to Inventory ({selectedRowKeys.length})
              </Button>
            )}

            {/* DRAFTS BUTTON */}
            <Button type="default" onClick={openDraftsModal} className="h-11 px-4 rounded-xl border-gray-300">
              Drafts
            </Button>

            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/add-product")} className="bg-blue-600 hover:bg-blue-700 border-0 h-11 px-6 rounded-xl font-medium">
              Add New Product
            </Button>
          </div>
        </div>

        {/* Selection Info Bar */}
        {selectedRowKeys.length > 0 && (
          <Card className="border-0 shadow-sm rounded-2xl bg-blue-50 border border-blue-200" styles={{ body: { padding: "16px 24px" } }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                <span className="font-medium text-blue-800">{selectedRowKeys.length} product(s) selected</span>
                <Button onClick={() => setSelectedRowKeys([])} size="small" className="border-blue-300 text-blue-600 hover:text-blue-800 hover:border-blue-400">
                  Clear Selection
                </Button>
              </div>
              <div className="text-sm text-blue-600">Click "Send to Inventory" to mark selected products as inventory parts</div>
            </div>
          </Card>
        )}

        {/* Filters Card */}
        <Card className="border-0 shadow-sm rounded-2xl bg-white overflow-hidden" styles={{ body: { padding: "24px" } }}>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input placeholder="Search products by name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} allowClear prefix={<SearchOutlined className="text-gray-400" />} className="w-full sm:flex-1 rounded-xl" size="large" />
            <Select placeholder="Sort by..." value={sortType} onChange={setSortType} allowClear className="w-full sm:w-48 rounded-xl" suffixIcon={<FilterOutlined className="text-gray-400" />} size="large">
              <Option value="created-desc">Newest First</Option>
              <Option value="created-asc">Oldest First</Option>
              <Option value="name-asc">Name A→Z</Option>
              <Option value="name-desc">Name Z→A</Option>
              <Option value="price-asc">Price low→high</Option>
              <Option value="price-desc">Price high→low</Option>
            </Select>
          </div>
        </Card>

        {/* Products Table Card */}
        <Card className="border-0 shadow-sm rounded-2xl bg-white overflow-hidden" styles={{ body: { padding: 0 } }}>
          <div className="relative">
            <Table
              columns={columns}
              dataSource={products}
              rowKey="id"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                onChange: (page) => fetchProducts(page),
                showSizeChanger: false,
                className: "px-6 py-4 border-t border-gray-200",
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} products`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 800 }}
              className="custom-table"
              rowClassName="hover:bg-gray-50 transition-colors"
            />
          </div>
        </Card>
      </div>

      {/* Product Details Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <InfoCircleOutlined className="text-green-600 text-lg" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900">Product Details</div>
              <div className="text-sm text-gray-500">Complete product information</div>
            </div>
          </div>
        }
        open={detailsVisible}
        onCancel={() => {
          setDetailsVisible(false);
          setSelectedProduct(null);
        }}
        width={700}
        className="rounded-2xl"
        footer={[
          <Button
            key="view"
            type="default"
            onClick={() => {
              if (selectedProduct) {
                const frontendUrl = `https://serenabeds.com/products/${selectedProduct.id}`;
                window.open(frontendUrl, "_blank");
              }
            }}
            className="px-6 rounded-lg h-10 border-blue-300 text-blue-600 hover:text-blue-700 hover:border-blue-400"
          >
            View on Website
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setDetailsVisible(false);
              setSelectedProduct(null);
            }}
            className="px-6 rounded-lg h-10 border-gray-300"
          >
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              setDetailsVisible(false);
              navigate(`/edit-product/${selectedProduct.id}`);
            }}
            className="px-6 rounded-lg h-10 bg-blue-600 hover:bg-blue-700 border-0 font-medium"
          >
            Edit Product
          </Button>,
        ]}
      >
        {selectedProduct && (
          <div className="mt-6 space-y-6">
            {/* Product Image and Basic Info */}
            <div className="flex items-start space-x-4">
              {selectedProduct.product_image ? (
                <Image src={selectedProduct.product_image} preview={false} style={{ width: 120, height: 120, borderRadius: 12, objectFit: "cover" }} fallback="https://via.placeholder.com/120" />
              ) : (
                <Avatar size={120} className="bg-gradient-to-r from-blue-500 to-blue-600 shadow-md text-white text-2xl" icon={getInitials(selectedProduct.product_name)} />
              )}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedProduct.product_name}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-600 text-sm">SKU:</span>
                    <p className="font-medium">{selectedProduct.sku || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-gray-600 text-sm">Status:</span>
                    <Tag color={selectedProduct.status === "active" ? "green" : "red"} className="ml-1">
                      {selectedProduct.status || "Unknown"}
                    </Tag>
                  </div>
                </div>

                {selectedProduct.isInventoryPart && (
                  <div className="mt-2">
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Already in Inventory
                    </Tag>
                  </div>
                )}

                <div className="mt-3">
                  <span className="text-gray-600 text-sm">Product Link:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <a href={`https://serenabeds.com/products/${selectedProduct.id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-sm truncate flex items-center gap-1">
                      https://serenabeds.com/products/{selectedProduct.id}
                    </a>
                    <Tooltip title="Open in new tab">
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined className="text-blue-500" />}
                        onClick={() => {
                          const frontendUrl = `https://serenabeds.com/products/${selectedProduct.id}`;
                          window.open(frontendUrl, "_blank");
                        }}
                        className="hover:bg-blue-50 rounded-lg transition-colors"
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Information */}
            <Descriptions title="Stock Information" column={1} size="small" bordered>
              <Descriptions.Item label="Stock Quantity">
                <Tag color={getStockColor(selectedProduct.stock)}>{selectedProduct.stock !== null && selectedProduct.stock !== undefined ? selectedProduct.stock : "Unknown"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Stock Status">
                <Tag color={getStockColor(selectedProduct.stock)}>{getStockText(selectedProduct.stock)}</Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* Pricing Information */}
            <Descriptions title="Pricing Information" column={2} size="small" bordered>
              <Descriptions.Item label="Cost Price">
                <div className="flex items-center space-x-1">
                  <EuroOutlined className="text-gray-400" />
                  <span className="font-semibold">{formatCurrency(selectedProduct.factory_price)}</span>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Sale Price">
                <div className="flex items-center space-x-1">
                  <EuroOutlined className="text-green-500" />
                  <span className="font-semibold text-green-600">{formatCurrency(selectedProduct.price_euro)}</span>
                </div>
              </Descriptions.Item>
            </Descriptions>

            {/* Product Types */}
            <Descriptions title="Product Types" column={1} size="small" bordered>
              <Descriptions.Item label="Types">
                <div className="flex flex-wrap gap-1">
                  {Array.isArray(selectedProduct.types) && selectedProduct.types.length > 0 ? (
                    selectedProduct.types.map((t, i) => {
                      const s = String(t || "").toLowerCase();
                      const label = s.includes("non") && s.includes("ottoman") ? "Non-Ottoman" : s.includes("ottoman") ? "Ottoman" : s.includes("cloud") ? "Cloud" : t || "-";
                      return (
                        <div key={`${t}-${i}`} className="text-xs bg-gray-100 text-gray-800 rounded px-2 py-1 border border-gray-200">
                          {label}
                        </div>
                      );
                    })
                  ) : (
                    <span className="text-gray-400">No types specified</span>
                  )}
                </div>
              </Descriptions.Item>
            </Descriptions>

            {/* Description */}
            <Descriptions title="Description" column={1} size="small" bordered>
              <Descriptions.Item>
                <div className="max-h-32 overflow-y-auto">{selectedProduct.description ? <div dangerouslySetInnerHTML={{ __html: selectedProduct.description }} /> : <span className="text-gray-400">No description available</span>}</div>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Quick Edit Modal - now includes editable sizes table */}
      <Modal
        title={
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <EditOutlined className="text-blue-600 text-lg" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900">Quick Edit Product</div>
              <div className="text-sm text-gray-500">Update basic product information & per-size details</div>
            </div>
          </div>
        }
        open={editVisible}
        onCancel={() => {
          setEditVisible(false);
          form.resetFields();
          setEditing(null);
          setQuickDesc("");
          setEditLoading(false);
          setInitialLoadDone(false);
          setQuickSizes([]);
        }}
        width={900}
        className="rounded-2xl"
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setEditVisible(false);
              form.resetFields();
              setEditing(null);
              setQuickDesc("");
              setEditLoading(false);
              setInitialLoadDone(false);
              setQuickSizes([]);
            }}
            className="px-6 rounded-lg h-10 border-gray-300"
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveQuickEdit}
            loading={editLoading}
            className="px-6 rounded-lg h-10 bg-blue-600 hover:bg-blue-700 border-0 font-medium"
          >
            Save Changes
          </Button>,
        ]}
      >
        {/* Show spinner while loading product details */}
        {editLoading && !initialLoadDone ? (
          <div className="flex flex-col items-center justify-center py-10">
            <Spin size="large" />
            <span className="mt-3 text-gray-600">Loading product details...</span>
          </div>
        ) : (
          <Form form={form} layout="vertical" className="mt-4 space-y-4">
            <Form.Item
              name="product_name"
              label="Product Name"
              rules={[{ required: true, message: "Product Name is required" }]}
            >
              <Input className="rounded-lg h-11" size="large" placeholder="Enter product name" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[
                {
                  validator: (_, value) => {
                    const plain = (value || quickDesc || "").replace(/<[^>]*>/g, "").trim();
                    if (!plain) {
                      return Promise.reject(new Error("Description is required"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <ReactQuill
                value={quickDesc}
                onChange={handleQuillChange}
                modules={quillModules}
                theme="snow"
                className="quill-quick"
                placeholder="Enter product description..."
              />
            </Form.Item>

            {/* SIZES GRID */}
            <div>
              <h4 className="font-semibold mb-3">Stock & Dimensions Per Size</h4>
              <div className="border rounded-lg p-3 bg-white">
                <div className="grid grid-cols-12 gap-3 text-sm font-medium items-center mb-2">
                  <div className="col-span-4">Variant</div>
                  <div className="col-span-2">Stock</div>
                  <div className="col-span-2">Length (cm)</div>
                  <div className="col-span-2">Width (cm)</div>
                  <div className="col-span-2">Height (cm)</div>
                </div>

                <div className="space-y-2 sizes-grid">
                  {quickSizes.map((row) => (
                    <div key={row.sizeId} className="grid grid-cols-12 gap-3 items-center">
                      {/* Variant */}
                      <div className="col-span-4">
                        <div className="text-sm font-medium">{row.label}</div>
                      </div>

                      {/* Stock */}
                      <div className="col-span-2">
                        <InputNumber
                          min={0}
                          max={99999}
                          value={row.stock}
                          onChange={(v) =>
                            updateQuickSizeRow(row.sizeId, { stock: Number(v || 0) })
                          }
                          className="w-full"
                        />
                      </div>

                      {/* Length */}
                      <div className="col-span-2">
                        <Input
                          value={row.length}
                          onChange={(e) =>
                            updateQuickSizeRow(row.sizeId, { length: e.target.value })
                          }
                          placeholder="e.g., 200"
                        />
                      </div>

                      {/* Width */}
                      <div className="col-span-2">
                        <Input
                          value={row.width}
                          onChange={(e) =>
                            updateQuickSizeRow(row.sizeId, { width: e.target.value })
                          }
                          placeholder="e.g., 150"
                        />
                      </div>

                      {/* Height */}
                      <div className="col-span-2">
                        <Input
                          value={row.height}
                          onChange={(e) =>
                            updateQuickSizeRow(row.sizeId, { height: e.target.value })
                          }
                          placeholder="e.g., 30"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 text-sm text-gray-600">
                  <strong>Total stock:</strong> {computeTotalStock()}
                </div>
              </div>
            </div>
          </Form>
        )}
      </Modal>

      {/* Drafts modal */}
      <Modal title="Drafts" open={draftsModalOpen} onCancel={() => setDraftsModalOpen(false)} footer={null} width={700}>
        {loadingDrafts ? (
          <div className="py-8 flex justify-center"><Spin /></div>
        ) : (
          <div className="space-y-3">
            {draftsList.length === 0 ? <div className="text-center text-gray-500">No drafts found</div> :
              draftsList.map((d) => {
                const id = d.id || d.draftId || d._id;
                const updatedAt = d.updatedAt || d.updated_at || d.updated || d.createdAt || d.created_at;
                return (
                  <div key={id} className="flex items-center justify-between p-3 border rounded-md">
                    <div>
                      <div className="font-medium">{d.title || "Untitled draft"}</div>
                      <div className="text-xs text-gray-500">Updated: {updatedAt ? new Date(updatedAt).toLocaleString() : "Unknown"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                     <Button onClick={() => continueDraft(d)} type="primary">Continue</Button>
                      <Button onClick={() => deleteDraft(id)} danger>Delete</Button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ProductTable;
